import { AlertTriangle, Check, X, ArrowUp, ArrowDown } from "lucide-react";

export default function AdminErrorReportsTab({
  filteredErrorReports,
  sortConfig,
  handleSort,
  handleResolveErrorReport,
  processing
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" /> Error Reports
            </h3>
            <p className="text-sm text-gray-500 mt-1">User-submitted corrections and system fault reports</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Sort:</span>
            <button
              onClick={() => handleSort('errorReports', 'source')}
              className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${sortConfig.errorReports?.field === 'source' ? 'bg-brand/10 text-brand' : 'bg-gray-100 text-gray-600'}`}
              data-testid="sort-errors-source"
            >
              Source
              {sortConfig.errorReports?.field === 'source' && (
                sortConfig.errorReports.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
              )}
            </button>
            <button
              onClick={() => handleSort('errorReports', 'venue_name')}
              className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${sortConfig.errorReports?.field === 'venue_name' ? 'bg-brand/10 text-brand' : 'bg-gray-100 text-gray-600'}`}
              data-testid="sort-errors-venue"
            >
              Venue
              {sortConfig.errorReports?.field === 'venue_name' && (
                sortConfig.errorReports.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
              )}
            </button>
            <button
              onClick={() => handleSort('errorReports', 'status')}
              className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${sortConfig.errorReports?.field === 'status' ? 'bg-brand/10 text-brand' : 'bg-gray-100 text-gray-600'}`}
              data-testid="sort-errors-status"
            >
              Status
              {sortConfig.errorReports?.field === 'status' && (
                sortConfig.errorReports.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
              )}
            </button>
            <button
              onClick={() => handleSort('errorReports', 'submitted_at')}
              className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${sortConfig.errorReports?.field === 'submitted_at' ? 'bg-brand/10 text-brand' : 'bg-gray-100 text-gray-600'}`}
              data-testid="sort-errors-date"
            >
              Date
              {sortConfig.errorReports?.field === 'submitted_at' && (
                sortConfig.errorReports.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
              )}
            </button>
          </div>
        </div>
      </div>
      {filteredErrorReports.length === 0 ? (
        <div className="p-8 text-center text-gray-500">No error reports</div>
      ) : (
        <div className="divide-y divide-gray-100">
          {filteredErrorReports.map((report) => {
            const isSystemReport = report.source === 'system';
            return (
              <div 
                key={report.id} 
                className={`p-4 ${
                  isSystemReport 
                    ? (report.status === 'pending' ? 'bg-red-50/50 border-l-4 border-red-400' : 'bg-gray-50/50 border-l-4 border-gray-300')
                    : (report.status === 'pending' ? 'bg-orange-50/50' : 'bg-gray-50/50')
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {isSystemReport ? (
                        <span className="px-2 py-0.5 bg-red-600 text-white rounded text-xs font-bold flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> SYSTEM
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                          User Report
                        </span>
                      )}
                      <h4 className="font-medium text-gray-900">
                        {isSystemReport ? (report.component || 'System Fault') : report.venue_name}
                      </h4>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        report.status === 'pending' ? 'bg-orange-100 text-orange-700' :
                        report.status === 'resolved' ? 'bg-green-100 text-green-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {report.status}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-xs ${isSystemReport ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                        {(report.fault_type || report.error_type)?.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{report.message}</p>
                    {isSystemReport && report.url && (
                      <p className="text-xs text-gray-400 mt-1 truncate">URL: {report.url}</p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      {!isSystemReport && report.reporter_email && <span>From: {report.reporter_email}</span>}
                      {isSystemReport && report.user_agent && (
                        <span className="truncate max-w-xs" title={report.user_agent}>
                          Browser: {report.user_agent.slice(0, 50)}...
                        </span>
                      )}
                      <span>{new Date(report.submitted_at).toLocaleDateString()} {new Date(report.submitted_at).toLocaleTimeString()}</span>
                    </div>
                  </div>
                  {report.status === 'pending' && (
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => handleResolveErrorReport(report.id, 'resolved')}
                        disabled={processing === report.id}
                        className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                        data-testid={`resolve-report-${report.id}`}
                      >
                        <Check className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleResolveErrorReport(report.id, 'dismissed')}
                        disabled={processing === report.id}
                        className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300 disabled:opacity-50"
                        data-testid={`dismiss-report-${report.id}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
